/**
 * Patient Dashboard Zustand Store
 *
 * Centralized state management for patient dashboard data including:
 * - Patient profile
 * - Patient referral data
 * - Appointments
 * - Photo URLs
 *
 * Replaces multiple hooks with a single unified store for better
 * performance and state coordination.
 *
 * @module stores/patientDashboardStore
 */

import { create } from 'zustand';
import { apiService } from '@/services/ApiService';
import { patientReferralAdapter } from '@/services/adapters/PatientReferralAdapter';
import { UserDomain } from '@/services/domains/UserDomain';
import { getLogger } from '@/services/shared/LoggerFactory';
import type { PatientProfile } from '@/services/domains/PatientDomain';
import type {
  PatientReferralData,
  PatientDashboardDietician,
  PatientReferralApiError,
} from '@/types/patientDashboard';
import type {
  CalendlyAppointment,
  CalendlyAppointmentsResponse,
} from '@/types/calendlyAppointments';
import { CalendlyAppointmentApiError } from '@/types/calendlyAppointments';

type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

interface PatientDashboardState {
  // Patient Profile
  patientProfile: PatientProfile | null;
  patientProfileStatus: AsyncStatus;
  patientProfileError: string | null;

  // Referral Data
  referralData: PatientReferralData | null;
  dietician: PatientDashboardDietician | null;
  referralStatus: AsyncStatus;
  referralError: PatientReferralApiError | null;
  hasActiveReferral: boolean;
  isExpired: boolean;
  daysUntilExpiry: number | null;
  canSchedule: boolean;

  // Appointments
  appointments: CalendlyAppointment[];
  upcomingAppointment: CalendlyAppointment | null;
  appointmentsStatus: AsyncStatus;
  appointmentsError: CalendlyAppointmentApiError | null;

  // Photo URLs
  photoUrls: Record<string, string | null>;
  photoUrlsLoading: boolean;
  photoUrlsError: string | null;

  // Initialization tracking
  isInitialized: boolean;
}

interface PatientDashboardActions {
  // Patient Profile Actions
  fetchPatientProfile: () => Promise<void>;

  // Referral Actions
  fetchReferralData: () => Promise<void>;

  // Appointments Actions
  fetchAppointments: () => Promise<void>;

  // Combined Actions
  fetchAllData: () => Promise<void>;
  refetchAll: () => Promise<void>;

  // Error Management
  clearPatientProfileError: () => void;
  clearReferralError: () => void;
  clearAppointmentsError: () => void;
  clearAllErrors: () => void;

  // Reset
  reset: () => void;
}

type PatientDashboardStore = PatientDashboardState & PatientDashboardActions;

const initialState: PatientDashboardState = {
  patientProfile: null,
  patientProfileStatus: 'loading',
  patientProfileError: null,

  referralData: null,
  dietician: null,
  referralStatus: 'loading',
  referralError: null,
  hasActiveReferral: false,
  isExpired: false,
  daysUntilExpiry: null,
  canSchedule: false,

  appointments: [],
  upcomingAppointment: null,
  appointmentsStatus: 'loading',
  appointmentsError: null,

  photoUrls: {},
  photoUrlsLoading: false,
  photoUrlsError: null,

  isInitialized: false,
};

const logger = getLogger();

/**
 * Find the earliest upcoming appointment from the list
 */
function findUpcomingAppointment(appointments: CalendlyAppointment[]): CalendlyAppointment | null {
  const now = new Date();

  const upcomingAppointments = appointments.filter(appointment => {
    const startTime = new Date(appointment.startTime);
    return appointment.eventStatus === 'active' &&
           appointment.inviteeStatus === 'active' &&
           startTime > now;
  });

  if (upcomingAppointments.length === 0) {
    return null;
  }

  return upcomingAppointments.sort((a, b) => {
    const timeA = new Date(a.startTime).getTime();
    const timeB = new Date(b.startTime).getTime();
    return timeA - timeB;
  })[0];
}

/**
 * Compute referral derived state
 */
function computeReferralDerivedState(referralData: PatientReferralData | null) {
  if (!referralData) {
    return {
      hasActiveReferral: false,
      isExpired: false,
      daysUntilExpiry: null,
      canSchedule: false,
    };
  }

  try {
    const expired = UserDomain.isReferralExpired(referralData);
    const hasActive = !expired && (referralData.status === 'claimed' || referralData.status === 'scheduled');
    const daysLeft = UserDomain.getDaysUntilExpiry(referralData);
    const canSched = UserDomain.canScheduleAppointments(referralData);

    return {
      hasActiveReferral: typeof hasActive === 'boolean' ? hasActive : false,
      isExpired: typeof expired === 'boolean' ? expired : false,
      daysUntilExpiry: typeof daysLeft === 'number' && !isNaN(daysLeft) ? daysLeft : null,
      canSchedule: typeof canSched === 'boolean' ? canSched : false,
    };
  } catch (error) {
    logger.error('Error computing referral derived state:', error);
    return {
      hasActiveReferral: false,
      isExpired: false,
      daysUntilExpiry: null,
      canSchedule: false,
    };
  }
}

export const usePatientDashboardStore = create<PatientDashboardStore>((set, get) => ({
  ...initialState,

  fetchPatientProfile: async () => {
    set({ patientProfileStatus: 'loading', patientProfileError: null });

    try {
      const profile = await apiService.patient.getProfile();
      set({
        patientProfile: profile,
        patientProfileStatus: 'success',
        patientProfileError: null,
      });
    } catch (error) {
      logger.error('Failed to fetch patient profile:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load patient profile';
      set({
        patientProfileError: errorMessage,
        patientProfileStatus: 'error',
      });
    }
  },

  fetchReferralData: async () => {
    set({ referralStatus: 'loading', referralError: null });

    try {
      const response = await patientReferralAdapter.getPatientReferrals({
        page: 1,
        perPage: 20,
      });

      if (response.referrals.length > 0) {
        const referral = response.referrals[0];
        const dieticianInfo = UserDomain.extractDieticianFromReferral(referral);
        const derivedState = computeReferralDerivedState(referral);

        // Collect user IDs for photo URLs
        const userIds: string[] = [];
        if (referral.referringPhysicianId) userIds.push(referral.referringPhysicianId);
        if (referral.dietitianId) userIds.push(referral.dietitianId);
        if (referral.claimedByUserId) userIds.push(referral.claimedByUserId);

        // Fetch photo URLs if we have user IDs
        if (userIds.length > 0) {
          set({ photoUrlsLoading: true, photoUrlsError: null });
          try {
            const photoResponse = await apiService.photo.getPhotoUrls({ user_ids: userIds });
            set({
              photoUrls: photoResponse.photo_urls,
              photoUrlsLoading: false,
            });
          } catch (err) {
            logger.error('Failed to fetch photo URLs:', err);
            set({
              photoUrlsError: 'Failed to load photo URLs',
              photoUrlsLoading: false,
            });
          }
        }

        set({
          referralData: referral,
          dietician: dieticianInfo,
          referralStatus: 'success',
          ...derivedState,
        });
      } else {
        // No active referral
        const derivedState = computeReferralDerivedState(null);
        set({
          referralData: null,
          dietician: null,
          referralStatus: 'success',
          photoUrls: {},
          ...derivedState,
        });
      }
    } catch (error) {
      logger.error('Failed to fetch referral data:', error);
      const apiError: PatientReferralApiError = {
        message: error instanceof Error ? error.message : 'Failed to fetch referral data',
        code: 'FETCH_ERROR',
        status: error instanceof Error && 'status' in error ? (error as { status: number }).status : undefined,
      };
      const derivedState = computeReferralDerivedState(null);
      set({
        referralError: apiError,
        referralStatus: 'error',
        referralData: null,
        dietician: null,
        photoUrls: {},
        ...derivedState,
      });
    }
  },

  fetchAppointments: async () => {
    set({ appointmentsStatus: 'loading', appointmentsError: null });

    try {
      const response: CalendlyAppointmentsResponse = await apiService.calendly.getMyAppointments({
        page: 1,
        perPage: 50,
        sortBy: 'start_time',
        sortOrder: 'asc',
      });

      const appointments = response.data || [];
      const upcomingAppointment = findUpcomingAppointment(appointments);

      set({
        appointments,
        upcomingAppointment,
        appointmentsStatus: 'success',
      });
    } catch (error) {
      logger.error('Failed to fetch appointments:', error);
      const apiError = error instanceof CalendlyAppointmentApiError
        ? error
        : new CalendlyAppointmentApiError(
            'Failed to load appointments. Please try again.',
            undefined,
            {},
            'UNKNOWN_ERROR'
          );

      set({
        appointmentsError: apiError,
        appointmentsStatus: 'error',
        appointments: [],
        upcomingAppointment: null,
      });
    }
  },

  fetchAllData: async () => {
    const { fetchPatientProfile, fetchReferralData, fetchAppointments } = get();

    await Promise.all([
      fetchPatientProfile(),
      fetchReferralData(),
      fetchAppointments(),
    ]);

    set({ isInitialized: true });
  },

  refetchAll: async () => {
    const { fetchAllData, clearAllErrors } = get();
    clearAllErrors();
    await fetchAllData();
  },

  clearPatientProfileError: () => {
    set({ patientProfileError: null });
  },

  clearReferralError: () => {
    set({ referralError: null });
  },

  clearAppointmentsError: () => {
    set({ appointmentsError: null });
  },

  clearAllErrors: () => {
    set({
      patientProfileError: null,
      referralError: null,
      appointmentsError: null,
      photoUrlsError: null,
    });
  },

  reset: () => {
    set(initialState);
  },
}));

/**
 * Selectors for optimized component rendering
 */
export const selectPatientProfile = (state: PatientDashboardStore) => state.patientProfile;
export const selectReferralData = (state: PatientDashboardStore) => state.referralData;
export const selectDietician = (state: PatientDashboardStore) => state.dietician;
export const selectUpcomingAppointment = (state: PatientDashboardStore) => state.upcomingAppointment;
export const selectHasActiveReferral = (state: PatientDashboardStore) => state.hasActiveReferral;
export const selectCanSchedule = (state: PatientDashboardStore) => state.canSchedule;
export const selectPhotoUrls = (state: PatientDashboardStore) => state.photoUrls;

export const selectIsLoading = (state: PatientDashboardStore) =>
  state.patientProfileStatus === 'loading' ||
  state.referralStatus === 'loading' ||
  state.appointmentsStatus === 'loading';

export const selectHasError = (state: PatientDashboardStore) =>
  state.patientProfileError !== null ||
  state.referralError !== null ||
  state.appointmentsError !== null;

export const selectPrimaryError = (state: PatientDashboardStore) =>
  state.patientProfileError || state.referralError?.message || state.appointmentsError?.message;
